import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";

import { useNodeTypeSelector } from "./library/index.ts";
import { useGetIsNodeSelected, useSetSelectedNodeId } from "./selection/useSelectedNode.ts";
import {
  applyDefinitionSelector,
  createNodeSelector,
  designerNodeConfigOrNullSelector,
  designerNodeImmediateChildrenSelector,
  parentContextSelector,
  useMutateNodeDesigns,
  useNautilusSelector,
} from "./state/index.ts";
import type {
  AddChildNodeImportOptions,
  DesignerComponentProps,
  DesignerNode,
  DesignerNodeHandle,
  DesignerNodeView,
  NodeDesignerDescr,
  NodeDesignerViewMode,
  NodeSelector,
  ReplaceChildNodeImportOptions,
} from "./types/index.ts";

declare global {
  interface Window {
    interactDebugMode: boolean | undefined;
    interactDebug: (on: boolean) => void;
  }
}

type OnConfigChange = DesignerComponentProps<any>["onConfigChange"];
type OnRemove = DesignerComponentProps<any>["onRemove"];

// stable placeholder while rendering a just-deleted node (the hook returns
// null for it; nothing consumes this)
const EMPTY_CONFIG = {};

export type NodeDesignerProps = {
  // a reference is all the framework needs — config/childNodes are resolved
  // from state by id, so callers may pass full nodes or public child views
  node: DesignerNodeHandle;
  view?: DesignerNodeView;
  onConfigChange?: OnConfigChange;
  onRemove?: OnRemove;
  index?: number;
  mode?: NodeDesignerViewMode;
  children?: ReactNode;
  /**
   * When true, this NodeDesigner instance will render the properties panel content
   * for this node when selected. Use this to explicitly control which instance
   * owns properties when multiple views of the same node are rendered.
   * Defaults to false.
   */
  ownsProperties?: boolean;
};

type Options = Pick<NodeDesignerProps, "node" | "view" | "onConfigChange" | "onRemove" | "index" | "mode">;

export const useDesignerNodeComponentProps = <TDescr extends NodeDesignerDescr>({
  node,
  onConfigChange,
  onRemove,
  view,
  index,
  mode,
}: Options) => {
  const { id, typeId, title, role, owner, creator } = node;
  const { update, add, remove, replace, reorder, setRole } = useMutateNodeDesigns();
  const { openSelector } = useNodeTypeSelector();
  const setSelectedNodeId = useSetSelectedNodeId();
  const getIsSelected = useGetIsNodeSelected();

  const createNode = useNautilusSelector(createNodeSelector);
  const applyDefinition = useNautilusSelector(applyDefinitionSelector);
  // when a node is deleted it doesn't prevent re-renders which can happen in the meantime
  // so if it is just use the tree config (which might be briefly stale)
  const configOrNull = useNautilusSelector((state) => designerNodeConfigOrNullSelector(state, { nodeId: id }));
  const config = configOrNull ?? EMPTY_CONFIG;
  const childNodes = useNautilusSelector((state) => designerNodeImmediateChildrenSelector(state, { nodeId: id }));
  const parentContext = useNautilusSelector((state) => parentContextSelector(state, { nodeId: id }));

  const handleConfigChange: DesignerComponentProps<any>["onConfigChange"] = useCallback(
    (newConfig) => {
      update(id, newConfig);
      onConfigChange?.(newConfig);
    },
    [id, onConfigChange, update],
  );

  const handleAddChild = useCallback(
    (name: string, selector: NodeSelector, options: AddChildNodeImportOptions = {}) => {
      const { slotIndex, createArgs, onAdded } = options;
      const slotId = { parentId: id, name, index: slotIndex };

      if ("id" in selector) {
        // full node designer e.g. paste or from library
        add(selector, slotId);
        onAdded?.(selector);
        return selector;
      } else if ("typeId" in selector && selector.typeId) {
        // TODO: when do definitions, this will need query and if only one definition matches, create it here
        // otherwise create placeholder so the LA can select
        let newChildNode = createNode(selector.typeId, createArgs);

        if ("definitionId" in selector && selector.definitionId) {
          newChildNode = applyDefinition(newChildNode, selector.definitionId);
        }

        add(newChildNode, slotId);
        onAdded?.(newChildNode);
        return newChildNode;
      } else if ("criteria" in selector) {
        openSelector({ target: { slotId }, criteria: selector.criteria, onCompleted: onAdded });
        return undefined;
      } else {
        throw new Error("No node type selector");
      }
    },
    [add, applyDefinition, createNode, id, openSelector],
  );

  const handleRemoveChild: DesignerComponentProps<TDescr>["onRemoveChild"] = useCallback(
    (childId) => {
      if (getIsSelected(childId)) {
        setSelectedNodeId(undefined);
      }
      return remove(childId);
    },
    [getIsSelected, remove, setSelectedNodeId],
  );

  const handleReplaceChild = useCallback(
    (idToReplace: number, selector: NodeSelector, options: ReplaceChildNodeImportOptions = {}) => {
      const { createArgs, onReplaced } = options;
      if ("typeId" in selector && selector.typeId) {
        // TODO: when do definitions, this will need query and if only one definition matches, create it here
        // otherwise create placeholder so the LA can select
        const replacementNode = createNode(selector.typeId, createArgs);
        replace(idToReplace, replacementNode);
        onReplaced?.(replacementNode);
        return replacementNode;
      } else if ("criteria" in selector) {
        openSelector({
          target: { slotResidentNodeId: idToReplace },
          criteria: selector.criteria,
          onCompleted: onReplaced,
        });
        return undefined;
      } else {
        throw new Error("No node type selector");
      }
    },
    [createNode, openSelector, replace],
  );

  const handleReorder: DesignerComponentProps<TDescr>["onReorder"] = useCallback(
    (nodeId, toIndex) => {
      reorder(nodeId, toIndex);
    },
    [reorder],
  );

  const handleSetRole: DesignerComponentProps<TDescr>["onSetRole"] = useCallback(
    (newRole) => {
      setRole(id, newRole);
    },
    [id, setRole],
  );

  const componentProps = useMemo(
    () => ({
      id,
      typeId,
      config,
      parentContext,
      onConfigChange: handleConfigChange,
      onAddChild: handleAddChild,
      onRemoveChild: handleRemoveChild,
      onRemove,
      onReplaceChild: handleReplaceChild,
      onReorder: handleReorder,
      onSetRole: handleSetRole,
      childNodes,
      view,
      title: title ?? "Untitled",
      role,
      index,
      owner: owner ?? "user",
      creator: creator ?? "user",
      mode,
    }),
    [
      childNodes,
      config,
      creator,
      handleAddChild,
      handleConfigChange,
      handleRemoveChild,
      handleReorder,
      handleReplaceChild,
      handleSetRole,
      id,
      index,
      mode,
      onRemove,
      owner,
      parentContext,
      role,
      title,
      typeId,
      view,
    ],
  ) as DesignerComponentProps<any>; // ugh the onAddChild, onRemoveChild, etc. are too difficult to type to be worth it;

  // a deleted node doesn't prevent re-renders, which can happen before it
  // unmounts; render nothing for it rather than briefly-stale data
  if (configOrNull === null) {
    return null;
  }

  return componentProps;
};
